import React from 'react';
import { Box, Button, CircularProgress, LinearProgress } from '@mui/material';
import { Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { ChevronLeftSharp, ChevronRightSharp, CheckSharp } from '@mui-symbols-material/w300';
import { WizardNavigationProps } from '../types';

const BUTTON_WIDTH = 140;

const WizardNavigation = ({
    activeStep,
    totalSteps,
    onBack,
    onNext,
    onFinish,
    isNextDisabled = false,
    isNextLoading = false,
    isFinishDisabled = false,
    isFinishLoading = false,
    nextButtonText,
    finishButtonText,
    showProgress = true,
    alignWithStepper = false,
}: WizardNavigationProps) => {
    const { t } = useTranslation();

    const isFirstStep = activeStep === 0;
    const isLastStep = activeStep === totalSteps - 1;
    const safeTotal = Math.max(totalSteps, 1);
    const progressValue = Math.min(100, Math.max(0, ((activeStep + 1) / safeTotal) * 100));
    const progressVisible = showProgress && totalSteps > 1;
    // Center the buttons under the first/last icons of an alternativeLabel stepper,
    // whose icons sit at 100% / (2 * totalSteps) from each edge
    const stepperInset = `max(0px, calc(100% / ${2 * safeTotal} - ${BUTTON_WIDTH / 2}px))`;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: alignWithStepper ? stepperInset : 0 }}>
            <Box sx={{ width: BUTTON_WIDTH, flexShrink: 0 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={onBack}
                    startIcon={<ChevronLeftSharp />}
                    disabled={isFirstStep}
                    tabIndex={isFirstStep ? -1 : 0}
                    sx={{ visibility: isFirstStep ? 'hidden' : 'visible' }}
                    data-testid="wizard-back-btn"
                >
                    {t('Back')}
                </Button>
            </Box>

            {progressVisible && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        {t('Step {{current}} of {{total}}', { current: activeStep + 1, total: totalSteps })}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progressValue}
                        sx={{ width: '100%', borderRadius: 4, height: 6 }}
                    />
                </Box>
            )}

            <Box sx={{ width: BUTTON_WIDTH, flexShrink: 0 }}>
                {isLastStep ? (
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={onFinish}
                        disabled={isFinishDisabled || isFinishLoading}
                        endIcon={isFinishLoading ? <CircularProgress size={20} color="inherit" /> : <CheckSharp />}
                        data-testid="wizard-finish-btn"
                    >
                        {finishButtonText ?? t('Finish')}
                    </Button>
                ) : (
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={onNext}
                        disabled={isNextDisabled || isNextLoading}
                        endIcon={isNextLoading ? <CircularProgress size={20} color="inherit" /> : <ChevronRightSharp />}
                        data-testid="wizard-next-btn"
                    >
                        {nextButtonText ?? t('Next')}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default WizardNavigation;
